<template>
  <PageLayout :title="$t('dashboard.title')" :subtitle="$t('dashboard.subtitle')">
    <div v-if="loadError" class="state-error mb-30" role="alert">
      <div class="state-error__title">{{ $t('common.load_failed') }}</div>
      <button type="button" class="btn btn-sm btn-outline-danger" @click="retryLoad">{{ $t('common.retry') }}</button>
    </div>
    <div v-else class="row">
      <div v-for="stat in statCards" :key="stat.key" class="col-xl-3 col-md-6 mb-30">
        <div class="dashboard-stat-card" :class="`dashboard-stat-card--${stat.tone}`">
          <div class="dashboard-stat-card__icon"><i :class="stat.icon"></i></div>
          <div class="dashboard-stat-card__body">
            <div class="dashboard-stat-card__value">{{ stat.value }}</div>
            <div class="dashboard-stat-card__label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div v-if="!hideScheduler" class="col-lg-8 mb-30">
        <ContentCard>
          <div class="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 class="mb-0">{{ $t('dashboard.this_week') }}</h5>
              <small class="text-muted">{{ weekRangeLabel }}</small>
            </div>
            <router-link to="/posts/create" class="btn btn-sm btn-primary">
              <i class="fas fa-plus mr-1"></i> {{ $t('dashboard.new_post') }}
            </router-link>
          </div>

          <div class="week-grid">
            <button
              v-for="day in weekDays"
              :key="day.key"
              type="button"
              class="week-day"
              :class="{
                'is-today': day.isToday,
                'is-selected': day.key === selectedDayKey,
                'has-posts': day.posts.length > 0,
              }"
              @click="selectedDayKey = day.key"
            >
              <div class="week-day__name">{{ day.weekday }}</div>
              <div class="week-day__date">{{ day.dayNum }}</div>
              <div class="week-day__count">
                <span v-if="day.posts.length">{{ day.posts.length }}</span>
                <span v-else class="week-day__empty">-</span>
              </div>
              <div class="week-day__dots">
                <span
                  v-for="(post, i) in day.posts.slice(0, 3)"
                  :key="post.id"
                  class="week-dot"
                  :class="'tone-' + statusTone(post.status)"
                  :style="{ marginLeft: i ? '3px' : '0' }"
                />
              </div>
            </button>
          </div>
        </ContentCard>

        <ContentCard card-class="mt-3">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h5 class="mb-0">{{ selectedDayLabel }}</h5>
            <span class="badge badge-light">{{ selectedDayPosts.length }} {{ selectedDayPosts.length === 1 ? $t('common.upload') : $t('common.uploads') }}</span>
          </div>

          <div v-if="loading" class="state-loading py-4" role="status" aria-live="polite" aria-busy="true">{{ $t('common.loading') }}</div>
          <div v-else-if="selectedDayPosts.length === 0" class="empty-day text-center py-5">
            <i class="fas fa-calendar-plus fa-2x mb-3 text-muted"></i>
            <p class="text-muted mb-3">{{ $t('dashboard.no_uploads') }}</p>
            <router-link to="/posts/create" class="btn btn-outline-primary btn-sm">{{ $t('dashboard.schedule_post') }}</router-link>
          </div>
          <div v-else class="upload-list">
            <router-link
              v-for="post in selectedDayPosts"
              :key="post.id"
              :to="`/posts/${post.id}`"
              class="upload-item"
            >
              <div class="upload-item__media">
                <img v-if="isImage(post)" :src="post.media_urls[0]" alt="" />
                <div v-else class="upload-item__placeholder">
                  <i :class="typeIcon(post.type)"></i>
                </div>
              </div>
              <div class="upload-item__body">
                <div class="upload-item__meta">
                  <span class="upload-item__time">{{ formatTime(post) }}</span>
                  <span class="badge badge-light text-capitalize">{{ post.type }}</span>
                  <span class="badge" :class="'badge-' + statusTone(post.status)">{{ statusLabel(post.status) }}</span>
                </div>
                <div class="upload-item__caption">{{ truncate(post.caption, 90) }}</div>
                <div class="upload-item__account">@{{ post.social_account?.username || '-' }}</div>
              </div>
              <i class="fas fa-chevron-right upload-item__arrow"></i>
            </router-link>
          </div>
        </ContentCard>
      </div>

      <div :class="hideScheduler ? 'col-lg-12 mb-30' : 'col-lg-4 mb-30'">
        <ContentCard v-if="quickActions.length">
          <h5 class="mb-3">{{ $t('dashboard.quick_actions') }}</h5>
          <div class="quick-list">
            <router-link v-for="action in quickActions" :key="action.to" :to="action.to" class="quick-item">
              <span class="quick-item__icon"><i :class="action.icon"></i></span>
              <span>{{ action.label }}</span>
            </router-link>
          </div>
        </ContentCard>

        <ContentCard v-if="!hideScheduler" card-class="mt-3">
          <h5 class="mb-3">{{ $t('dashboard.week_summary') }}</h5>
          <div class="summary-row">
            <span>{{ $t('dashboard.scheduled') }}</span>
            <strong>{{ weekSummary.scheduled }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('dashboard.published') }}</span>
            <strong>{{ weekSummary.published }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('dashboard.drafts_with_date') }}</span>
            <strong>{{ weekSummary.draft }}</strong>
          </div>
          <div class="summary-row">
            <span>{{ $t('status.failed') }}</span>
            <strong class="text-danger">{{ weekSummary.failed }}</strong>
          </div>
        </ContentCard>
      </div>
    </div>
  </PageLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import i18nPage from '@/mixins/i18nPage'

export default {
  mixins: [i18nPage],
  data() {
    return {
      selectedDayKey: this.dayKey(new Date()),
      loadError: false,
    }
  },
  computed: {
    ...mapGetters('app', ['dashboard', 'loading', 'hideScheduler', 'hideAI']),
    upcomingPosts() {
      return this.dashboard?.upcoming_posts || []
    },
    weekDays() {
      const start = this.startOfToday()
      const days = []
      for (let i = 0; i < 7; i++) {
        const d = new Date(start)
        d.setDate(start.getDate() + i)
        const key = this.dayKey(d)
        days.push({
          key,
          date: d,
          weekday: d.toLocaleDateString(this.intlLocale, { weekday: 'short' }),
          dayNum: d.getDate(),
          isToday: i === 0,
          posts: this.postsForDay(key),
        })
      }
      return days
    },
    weekRangeLabel() {
      if (!this.weekDays.length) return ''
      const a = this.weekDays[0].date
      const b = this.weekDays[6].date
      const opts = { month: 'short', day: 'numeric' }
      return `${a.toLocaleDateString(this.intlLocale, opts)} – ${b.toLocaleDateString(this.intlLocale, { ...opts, year: 'numeric' })}`
    },
    selectedDay() {
      return this.weekDays.find((d) => d.key === this.selectedDayKey) || this.weekDays[0]
    },
    selectedDayPosts() {
      return this.selectedDay?.posts || []
    },
    selectedDayLabel() {
      if (!this.selectedDay) return this.$t('common.uploads')
      return this.selectedDay.date.toLocaleDateString(this.intlLocale, {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    },
    weekSummary() {
      const posts = this.upcomingPosts
      return {
        scheduled: posts.filter((p) => p.status === 'scheduled').length,
        published: posts.filter((p) => p.status === 'published').length,
        draft: posts.filter((p) => p.status === 'draft').length,
        failed: posts.filter((p) => p.status === 'failed').length,
      }
    },
    statCards() {
      const stats = this.dashboard?.post_stats || {}
      const analytics = this.dashboard?.analytics_overview || {}
      return [
        { key: 'accounts', label: this.$t('dashboard.accounts'), value: this.dashboard?.connected_count || 0, icon: 'fab fa-instagram', tone: 'primary' },
        { key: 'scheduled', label: this.$t('dashboard.scheduled'), value: stats.scheduled || 0, icon: 'fas fa-clock', tone: 'info' },
        { key: 'published', label: this.$t('dashboard.published'), value: stats.published || 0, icon: 'fas fa-check-circle', tone: 'success' },
        { key: 'impressions', label: this.$t('dashboard.impressions'), value: this.formatNum(analytics.total_impressions), icon: 'fas fa-eye', tone: 'warning' },
      ]
    },
    quickActions() {
      const actions = [
        { to: '/ai/studio', label: this.$t('nav.design_studio'), icon: 'fas fa-paint-brush' },
        { to: '/ai/studio/api', label: this.$t('dashboard.render_api'), icon: 'fas fa-plug' },
      ]
      if (!this.hideAI) {
        actions.push({ to: '/ai/assistant', label: this.$t('nav.assistant'), icon: 'fas fa-robot' })
      }
      if (!this.hideScheduler) {
        actions.push(
          { to: '/posts/create', label: this.$t('nav.create_post'), icon: 'fas fa-plus-circle' },
          { to: '/posts/calendar', label: this.$t('nav.calendar'), icon: 'fas fa-calendar-alt' },
          { to: '/analytics', label: this.$t('nav.analytics'), icon: 'fas fa-chart-bar' },
        )
      }
      return actions
    },
  },
  async created() {
    await this.retryLoad()
  },
  methods: {
    ...mapActions('app', ['fetchDashboard']),
    async retryLoad() {
      this.loadError = false
      try {
        await this.fetchDashboard()
      } catch {
        this.loadError = true
      }
    },
    startOfToday() {
      const d = new Date()
      d.setHours(0, 0, 0, 0)
      return d
    },
    dayKey(date) {
      const d = new Date(date)
      const pad = (n) => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    postDayKey(post) {
      const raw = post.scheduled_at || post.published_at
      return raw ? this.dayKey(raw) : null
    },
    postsForDay(key) {
      return this.upcomingPosts
        .filter((p) => this.postDayKey(p) === key)
        .sort((a, b) => {
          const ta = new Date(a.scheduled_at || a.published_at || 0).getTime()
          const tb = new Date(b.scheduled_at || b.published_at || 0).getTime()
          return ta - tb
        })
    },
    formatTime(post) {
      const raw = post.scheduled_at || post.published_at
      if (!raw) return '-'
      return new Date(raw).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    statusTone(status) {
      const map = {
        scheduled: 'primary',
        published: 'success',
        draft: 'secondary',
        failed: 'danger',
        publishing: 'info',
      }
      return map[status] || 'secondary'
    },
    typeIcon(type) {
      if (type === 'story') return 'fas fa-circle'
      if (type === 'reel') return 'fas fa-film'
      return 'fas fa-image'
    },
    isImage(post) {
      const url = post.media_urls?.[0] || ''
      return url && !/\.(mp4|mov)(\?|$)/i.test(url)
    },
    truncate(text, len) {
      if (!text) return this.$t('common.no_caption')
      return text.length > len ? `${text.slice(0, len)}…` : text
    },
    formatNum(n) {
      if (!n) return '0'
      if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
      return n.toLocaleString()
    },
  },
}
</script>

<style scoped>
.week-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.5rem;
}
.week-day {
  border: 1px solid #e8ecef;
  background: #fff;
  border-radius: 12px;
  padding: 0.75rem 0.4rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  min-height: 96px;
}
.week-day:hover { border-color: var(--wc-primary); }
.week-day.is-today { background: #f3fffb; }
.week-day.is-selected {
  border-color: var(--wc-primary);
  box-shadow: 0 0 0 2px rgba(var(--wc-primary-rgb), 0.18);
  background: #f0fffa;
}
.week-day__name {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--wc-text-muted);
  font-weight: 600;
}
.week-day__date {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3340;
  line-height: 1.3;
}
.week-day__count {
  font-size: 0.75rem;
  color: var(--wc-primary);
  font-weight: 600;
  min-height: 1.1em;
}
.week-day__empty { color: #c5cad3; font-weight: 400; }
.week-day__dots {
  margin-top: 0.35rem;
  min-height: 8px;
  display: flex;
  justify-content: center;
}
.week-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #adb5bd;
  display: inline-block;
}
.week-dot.tone-primary { background: #4798e8; }
.week-dot.tone-success { background: #22c6ab; }
.week-dot.tone-danger { background: #ef6e6e; }
.week-dot.tone-secondary { background: #adb5bd; }
.week-dot.tone-info { background: #17a2b8; }

.upload-list { display: flex; flex-direction: column; gap: 0.65rem; }
.upload-item {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem;
  border: 1px solid #eef1f4;
  border-radius: 12px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.upload-item:hover {
  border-color: var(--wc-primary);
  box-shadow: 0 2px 10px rgba(0,0,0,0.04);
  text-decoration: none;
  color: inherit;
}
.upload-item__media {
  width: 56px;
  height: 56px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
  background: #f1f3f5;
}
.upload-item__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.upload-item__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wc-text-muted);
}
.upload-item__body { flex: 1; min-width: 0; }
.upload-item__meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 0.25rem;
}
.upload-item__time {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--wc-primary);
  margin-right: 0.25rem;
}
.upload-item__caption {
  font-size: 0.9rem;
  color: #2c3340;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.upload-item__account {
  font-size: 0.75rem;
  color: var(--wc-text-muted);
  margin-top: 0.15rem;
}
.upload-item__arrow { color: #c5cad3; font-size: 0.75rem; }

.quick-list { display: flex; flex-direction: column; gap: 0.4rem; }
.quick-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  color: #495057;
  text-decoration: none;
  transition: background 0.15s, color 0.15s;
}
.quick-item:hover {
  background: #f3fffb;
  color: var(--wc-primary);
  text-decoration: none;
}
.quick-item__icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--wc-primary);
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.55rem 0;
  border-bottom: 1px solid #f0f2f5;
  font-size: 0.9rem;
  color: #6c757d;
}
.summary-row:last-child { border-bottom: 0; }
.summary-row strong { color: #2c3340; }

.empty-day { color: var(--wc-text-muted); }

@media (max-width: 767px) {
  .week-grid { gap: 0.35rem; }
  .week-day { padding: 0.5rem 0.2rem; min-height: 84px; }
  .week-day__date { font-size: 1rem; }
}
</style>
